import { useState, useRef, useCallback, useEffect } from 'react';
import { ZoomPreset } from '../../../components/VideoPlayer';
import { MetronomeLEDs } from '../../../components/MetronomeLEDs';
import { MetronomeControls } from '../../../components/MetronomeControls';
import { TempoControl } from '../../../components/TempoControl';
import { ZoomRegion } from '../../../types/capsule';

interface CapsuleVideoControlsProps {
  videoPlaying: boolean;
  videoReady: boolean;
  onPlay: () => void;
  onPause: () => void;
  onStop: () => void;
  isBeatActive: boolean;
  isDownbeatActive: boolean;
  metronomeEnabled: boolean;
  metronomeMuted: boolean;
  onToggleMetronome: () => void;
  onToggleMute: () => void;
  currentTempo: number;
  scoreTempo: number;
  onTempoChange: (tempo: number) => void;
  zoomRegions?: { rightHand?: ZoomRegion; leftHand?: ZoomRegion };
  currentZoom: ZoomPreset;
  onZoomFull: () => void;
  onZoomRight: () => void;
  onZoomLeft: () => void;
  showScore: boolean;
  onToggleScore: () => void;
  videoSyncOffset: number;
  volume: number;
  onVolumeChange: (volume: number) => void;
}

const VOLUME_STORAGE_KEY = 'fingerstyle-video-volume';

function loadSavedVolume(): number {
  try {
    const saved = localStorage.getItem(VOLUME_STORAGE_KEY);
    if (saved !== null) {
      const val = parseFloat(saved);
      if (!isNaN(val) && val >= 0 && val <= 1) return val;
    }
  } catch {
    // localStorage unavailable
  }
  return 1;
}

export function CapsuleVideoControls({
  videoPlaying,
  videoReady,
  onPlay,
  onPause,
  onStop,
  isBeatActive,
  isDownbeatActive,
  metronomeEnabled,
  metronomeMuted,
  onToggleMetronome,
  onToggleMute,
  currentTempo,
  scoreTempo,
  onTempoChange,
  zoomRegions,
  currentZoom,
  onZoomFull,
  onZoomRight,
  onZoomLeft,
  showScore,
  onToggleScore,
  videoSyncOffset,
  volume,
  onVolumeChange,
}: CapsuleVideoControlsProps) {
  // Track volume before muting so we can restore it
  const volumeBeforeMuteRef = useRef<number>(loadSavedVolume());
  const [isMuted, setIsMuted] = useState(volume === 0);

  // Sync muted state when volume changes externally
  useEffect(() => {
    setIsMuted(volume === 0);
  }, [volume]);

  const handleToggleMute = useCallback(() => {
    if (isMuted) {
      // Unmute: restore previous volume
      const restored = volumeBeforeMuteRef.current > 0 ? volumeBeforeMuteRef.current : 1;
      onVolumeChange(restored);
      setIsMuted(false);
    } else {
      // Mute: save current volume and set to 0
      volumeBeforeMuteRef.current = volume;
      onVolumeChange(0);
      setIsMuted(true);
    }
  }, [isMuted, volume, onVolumeChange]);

  const handleVolumeSliderChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value) / 100;
    onVolumeChange(val);
    if (val > 0) {
      volumeBeforeMuteRef.current = val;
    }
  }, [onVolumeChange]);

  return (
    <div className="capsule-controls">
      <button
        onClick={videoPlaying ? onPause : onPlay}
        disabled={!videoReady}
        className={`capsule-btn ${videoPlaying ? 'capsule-btn--warning' : 'capsule-btn--primary'}`}
      >
        {videoPlaying ? 'Pause' : 'Play'}
      </button>
      {videoPlaying && (
        <button onClick={onStop} className="capsule-btn capsule-btn--ghost">
          Stop
        </button>
      )}

      <div className="capsule-controls-divider" />

      <MetronomeLEDs
        isBeatActive={isBeatActive}
        isDownbeatActive={isDownbeatActive}
        enabled={metronomeEnabled}
      />

      <MetronomeControls
        enabled={metronomeEnabled}
        muted={metronomeMuted}
        onToggle={onToggleMetronome}
        onMuteToggle={onToggleMute}
      />

      <div className="capsule-controls-divider" />

      <TempoControl
        currentTempo={currentTempo}
        scoreTempo={scoreTempo}
        onChange={onTempoChange}
        disabled={!videoReady}
      />

      <div className="capsule-volume">
        <button
          onClick={handleToggleMute}
          className="capsule-btn capsule-btn--compact capsule-btn--outline"
          title={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <line x1="23" y1="9" x2="17" y2="15" />
              <line x1="17" y1="9" x2="23" y2="15" />
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
            </svg>
          )}
        </button>
        <input
          type="range"
          min="0"
          max="100"
          value={Math.round(volume * 100)}
          onChange={handleVolumeSliderChange}
          className="capsule-volume-slider"
          title={`Volume: ${Math.round(volume * 100)}%`}
        />
      </div>

      <div className="capsule-zoom">
        <span className="capsule-zoom-label">Zoom:</span>
        <button
          onClick={onZoomFull}
          className={`capsule-btn capsule-btn--compact ${currentZoom === 'full' ? 'capsule-btn--active' : 'capsule-btn--outline'}`}
        >
          Full
        </button>
        <button
          onClick={onZoomRight}
          disabled={!zoomRegions?.rightHand}
          className={`capsule-btn capsule-btn--compact ${currentZoom === 'rightHand' ? 'capsule-btn--active' : 'capsule-btn--outline'}`}
        >
          Right
        </button>
        <button
          onClick={onZoomLeft}
          disabled={!zoomRegions?.leftHand}
          className={`capsule-btn capsule-btn--compact ${currentZoom === 'leftHand' ? 'capsule-btn--active' : 'capsule-btn--outline'}`}
        >
          Left
        </button>
      </div>

      <button
        onClick={onToggleScore}
        className={`capsule-btn capsule-btn--compact ${showScore ? 'capsule-btn--outline-active' : 'capsule-btn--outline'}`}
      >
        Score
      </button>

      {videoSyncOffset !== 0 && (
        <span className="capsule-controls-text">
          (offset: {videoSyncOffset > 0 ? '+' : ''}{videoSyncOffset}ms)
        </span>
      )}
    </div>
  );
}
